import { FormEvent, useCallback, useState } from "react"
import toast from "react-hot-toast"
import {
  createStudentNote,
  updateStudentNote,
} from "../../../../api/studentNotes"
import { getLearnerApiErrorMessage } from "../../../../auth/learner/errors"
import {
  EMPTY_NOTE_DRAFT,
  type NoteCardViewModel,
  type NoteDraft,
} from "../types"

const draftFromCard = (card: NoteCardViewModel): NoteDraft => ({
  title: card.title,
  content: card.content,
  subject: card.subject,
  color: card.color,
})

const draftsEqual = (a: NoteDraft, b: NoteDraft) =>
  a.title === b.title &&
  a.content === b.content &&
  a.subject === b.subject &&
  a.color === b.color

const useNoteComposer = (
  onCreated: () => Promise<void> | void,
  onUpdated: () => Promise<void> | void
) => {
  const [isOpen, setIsOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<NoteDraft>(EMPTY_NOTE_DRAFT)
  const [baseline, setBaseline] = useState<NoteDraft>(EMPTY_NOTE_DRAFT)
  const [saving, setSaving] = useState(false)

  const isDirty = isOpen && !draftsEqual(draft, baseline)

  const open = useCallback(() => {
    setEditingId(null)
    setDraft(EMPTY_NOTE_DRAFT)
    setBaseline(EMPTY_NOTE_DRAFT)
    setIsOpen(true)
  }, [])

  const openEdit = useCallback((card: NoteCardViewModel) => {
    const next = draftFromCard(card)
    setEditingId(card.id)
    setDraft(next)
    setBaseline(next)
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    if (saving) return
    setIsOpen(false)
    setEditingId(null)
    setDraft(EMPTY_NOTE_DRAFT)
    setBaseline(EMPTY_NOTE_DRAFT)
  }, [saving])

  const patch = useCallback((patchValue: Partial<NoteDraft>) => {
    setDraft((prev) => ({ ...prev, ...patchValue }))
  }, [])

  const submit = useCallback(
    async (event?: FormEvent) => {
      event?.preventDefault()
      const title = draft.title.trim()
      const content = draft.content.trim()
      const subject = draft.subject.trim() || EMPTY_NOTE_DRAFT.subject
      if (!title || !content) {
        toast.error("Add a title and some note content")
        return
      }
      setSaving(true)
      try {
        const payload = {
          title,
          content,
          subject,
          color: draft.color,
        }
        if (editingId) {
          await updateStudentNote(editingId, payload)
          toast.success("Note updated")
          await onUpdated()
        } else {
          await createStudentNote(payload)
          toast.success("Note saved")
          await onCreated()
        }
        setDraft(EMPTY_NOTE_DRAFT)
        setEditingId(null)
        setIsOpen(false)
      } catch (err) {
        toast.error(
          getLearnerApiErrorMessage(
            err,
            editingId ? "Could not update note" : "Could not save note"
          )
        )
      } finally {
        setSaving(false)
      }
    },
    [draft, editingId, onCreated, onUpdated]
  )

  return {
    isOpen,
    draft,
    saving,
    editingId,
    isDirty,
    open,
    openEdit,
    close,
    patch,
    submit,
  }
}

export default useNoteComposer
